import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

// Color constants
const colors = {
    primary: '#0099CC',
    secondary: '#0D6380',
    accent: '#19C6FF',
    error: '#FF595E',
    info: '#45B4D9',
    success: '#8AC926',
    warning: '#FFCA3A',
    pink: '#FA0055',
    blue: '#2E62FF',
    purple: '#2B314A',
    light: '#FFFFFF',
    dark: '#000000',
    base: '#F7F7F7',
    baseLight: '#FAFAFA',
    grayDark: '#464D69',
    gray: '#3F3E3D',
    grayLight: '#A5A7B2',
    grayLighter: '#DDE6F3',
    darkBg: '#323130',
    darkBgLighter: '#444444',
    darkBgColor: '#FFFFFF',
    darkBgContainer: '#0A0A0A',
    darkBorderColor: 'rgba(0, 0, 0, 0.2)'
};

// Base theme configuration
const baseThemeConfig: ThemeOptions = {
    typography: {
        fontFamily: "'Wanzl_Etelka Light', 'Roboto', 'Helvetica', 'Arial', sans-serif",
        h1: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        h3: {
            fontSize: '1.375rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        h4: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.2,
            fontFamily: "'Wanzl_Etelka Medium', 'Roboto', 'Helvetica', 'Arial', sans-serif"
        },
        body1: {
            fontWeight: 500
        },
        body2: {
            fontWeight: 500
        }
    },
    shape: {
        borderRadius: 0
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    fontSize: '16px',
                    height: '100%',
                    fontFamily: "'Wanzl_Etelka Light', 'Montserrat', 'Roboto', sans-serif",
                    '@media (max-width: 1280px) and (min-width: 960px)': {
                        fontSize: '15px'
                    },
                    '@media (max-width: 959px) and (min-width: 320px)': {
                        fontSize: '14px'
                    }
                },
                body: {
                    margin: 0,
                    padding: 0,
                    height: '100%',
                    fontFamily: "'Wanzl_Etelka Light', 'Montserrat', 'Roboto', sans-serif",
                    fontWeight: 500,
                    overflowX: 'hidden',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    WebkitOverflowScrolling: 'touch',
                    overflowWrap: 'break-word',
                    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
                    WebkitTouchCallout: 'none',
                    WebkitTextSizeAdjust: '100%',
                    msTextSizeAdjust: '100%'
                },
                '#root': {
                    height: '100%'
                },
                // Custom scrollbar styles
                '::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px'
                },
                '::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px'
                },
                '::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                    transition: 'background 0.3s ease'
                },
                '::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8'
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    letterSpacing: 0,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '3px'
                },
                contained: {
                    '&:hover:before': {
                        opacity: 0.2
                    }
                },
                text: ({ ownerState, theme }) => ({
                    ...(ownerState.color === 'error' && {
                        color: theme.palette.error.main,
                        '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        }
                    })
                })
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
                    }
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent',
                    border: 'none',
                    '&::-webkit-scrollbar': {
                        width: '6px'
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '3px'
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)'
                    }
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backdropFilter: 'blur(8px)'
                }
            }
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0
                }
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    fontWeight: 400
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    lineHeight: 1.5
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 0
            }
        }
    }
};

// Light theme
const lightTheme: ThemeOptions = {
    ...baseThemeConfig,
    palette: {
        mode: 'light',
        primary: {
            main: colors.primary,
            dark: colors.secondary,
            light: colors.accent
        },
        secondary: {
            main: colors.secondary
        },
        error: {
            main: colors.error
        },
        warning: {
            main: colors.warning
        },
        info: {
            main: colors.info
        },
        success: {
            main: colors.success
        },
        background: {
            default: colors.base,
            paper: colors.light
        },
        text: {
            primary: colors.grayDark,
            secondary: colors.gray
        },
        divider: colors.grayLighter,
        grey: {
            50: colors.baseLight,
            100: colors.base,
            200: colors.grayLighter,
            300: colors.grayLight,
            400: colors.gray,
            500: colors.grayDark
        }
    }
};

// Dark theme
const darkTheme: ThemeOptions = {
    ...baseThemeConfig,
    palette: {
        mode: 'dark',
        primary: {
            main: colors.primary,
            dark: colors.secondary,
            light: colors.accent
        },
        secondary: {
            main: colors.secondary
        },
        error: {
            main: colors.error
        },
        warning: {
            main: colors.warning
        },
        info: {
            main: colors.info
        },
        success: {
            main: colors.success
        },
        background: {
            default: '#121212',
            paper: colors.darkBg
        },
        text: {
            primary: colors.darkBgColor,
            secondary: colors.grayLight
        },
        divider: colors.darkBorderColor
    },
    components: {
        ...baseThemeConfig.components,
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    fontSize: '16px',
                    height: '100%',
                    fontFamily: "'Wanzl_Etelka Light', 'Montserrat', 'Roboto', sans-serif",
                    '@media (max-width: 1280px) and (min-width: 960px)': {
                        fontSize: '15px'
                    },
                    '@media (max-width: 959px) and (min-width: 320px)': {
                        fontSize: '14px'
                    }
                },
                body: {
                    margin: 0,
                    padding: 0,
                    height: '100%',
                    fontFamily: "'Wanzl_Etelka Light', 'Montserrat', 'Roboto', sans-serif",
                    fontWeight: 500,
                    overflowX: 'hidden',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    WebkitOverflowScrolling: 'touch',
                    overflowWrap: 'break-word',
                    WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
                    WebkitTouchCallout: 'none',
                    WebkitTextSizeAdjust: '100%',
                    msTextSizeAdjust: '100%'
                },
                '#root': {
                    height: '100%'
                },
                // Dark mode scrollbar
                '@media (prefers-color-scheme: dark)': {
                    '::-webkit-scrollbar-track': {
                        background: '#2d2d2d'
                    },
                    '::-webkit-scrollbar-thumb': {
                        background: '#555'
                    },
                    '::-webkit-scrollbar-thumb:hover': {
                        background: '#777'
                    }
                },
                // Custom scrollbar styles
                '::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px'
                },
                '::-webkit-scrollbar-track': {
                    background: '#2d2d2d'
                },
                '::-webkit-scrollbar-thumb': {
                    background: '#555',
                    borderRadius: '4px',
                    transition: 'background 0.3s ease'
                },
                '::-webkit-scrollbar-thumb:hover': {
                    background: '#777'
                },
                // Dark theme specific styles
                '.theme--dark': {
                    '& *': {
                        color: `${colors.darkBgColor} !important`,
                        borderColor: `${colors.darkBorderColor} !important`
                    },
                    '& .app-card, & .dash-card, & .navbar-default, & .dash-card-wrap, & .dash-card-light, & .comp-full-calendar, & .session-block, & .card-base, & .inbox-listing ul > li:nth-child(2n), & .fixed-pricing, & .dropzone, & .card, & .inner-toolbar, & .v-window-item, & .v-step, & .v-expansion-panel-content__wrap, & .v-tabs > .v-tabs-bar':
                        {
                            backgroundColor: `${colors.darkBg} !important`,
                            borderColor: colors.darkBorderColor
                        },
                    '& .v-content__wrap': {
                        background: '#121212'
                    },
                    '& .Vuely-toolbar.v-toolbar': {
                        backgroundColor: `${colors.darkBg} !important`
                    }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.darkBg,
                    boxShadow: '0 1px 15px 1px rgba(0, 0, 0, 0.05)'
                }
            }
        }
    }
};

// Theme creator function
export const createAppTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
    const themeOptions = mode === 'dark' ? darkTheme : lightTheme;
    return createTheme(themeOptions);
};

// Export themes
export { lightTheme, darkTheme, colors };
